import { LitElement, html, css } from 'lit';

/**
 * <filter-keuze> — een keuzelijst met vinkjes voor het filter op de veldgegevens (labopdracht).
 *
 * Eén regel hoog, ook bij 90+ bijzondere bestanddelen: de keuzes zitten in het menu, met een
 * zoekveld bij lange lijsten, groepskopjes waar die er zijn, en onderin 'Laag bevat één ervan /
 * allemaal' waar dat iets betekent (niet bij één hoofdgrondsoort of de grondwaterligging: een
 * laag heeft er maar één van).
 *
 *   <filter-keuze leeg="Alle bijmengingen" meervoud="bijmengingen"
 *     .items=${[{ waarde, label, aantal, groep? }]} .gekozen=${['humeus']} modus="any"
 *     @wijzig=${(e) => … e.detail.gekozen, e.detail.modus}></filter-keuze>
 *
 * `modus` leeg = geen keuze één/allemaal. Elke wijziging gaat meteen door (het filter werkt
 * live); 'Klaar' sluit alleen het menu.
 *
 * Het menu staat in de bovenste laag van de browser (popover="manual"). Met alleen
 * position:fixed werd het afgesneden: het paneel eromheen is een CSS-container, en die vangt
 * fixed-elementen op; de kaart (Leaflet, z-index 400+) viel er dan ook overheen.
 */
export class FilterKeuze extends LitElement {
  static properties = {
    items: { attribute: false },
    gekozen: { attribute: false },
    leeg: { type: String },
    meervoud: { type: String },
    modus: { type: String },
    eenheid: { type: String },      // bij de aantallen: meervoud ('lagen')
    eenheidEen: { type: String },   // en enkelvoud ('laag')
    zoekLabel: { type: String },
    _open: { state: true },
    _zoek: { state: true },
    _plek: { state: true },
  };

  constructor() {
    super();
    this.items = [];
    this.gekozen = [];
    this.leeg = 'Alle';
    this.meervoud = 'gekozen';
    this.modus = '';
    this.eenheid = 'lagen';
    this.eenheidEen = 'laag';
    this.zoekLabel = 'Zoek';
    this._open = false;
    this._zoek = '';
    this._plek = null;
    this._buiten = (e) => { if (this._open && !e.composedPath().includes(this)) this._sluit(); };
    this._toets = (e) => { if (e.key === 'Escape' && this._open) this._sluit(); };
    this._herpositie = () => { if (this._open) this._zetPlek(); };
  }

  /**
   * Zolang het menu open is, volgt het de knop. Een scroll-gebeurtenis is hier niet genoeg: de
   * pagina scrolt binnen een shadow DOM, en die gebeurtenis komt niet bij window aan -- het menu
   * bleef dan op zijn plek hangen terwijl de knop wegscrolde (30-10-2026). Dus per beeld kijken
   * of de knop verschoven is. Scrolt de knop helemaal uit beeld, dan gaat het menu dicht.
   */
  _volg() {
    cancelAnimationFrame(this._frame);
    let vorige = '';
    const stap = () => {
      if (!this._open) return;
      const r = this.renderRoot.querySelector('.knop')?.getBoundingClientRect();
      if (!r) return;
      if (r.bottom < 0 || r.top > window.innerHeight) { this._sluit(); return; }
      const nu = `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.bottom)}`;
      if (nu !== vorige) { vorige = nu; this._zetPlek(); }
      this._frame = requestAnimationFrame(stap);
    };
    this._frame = requestAnimationFrame(stap);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._buiten, true);
    document.addEventListener('keydown', this._toets);
    window.addEventListener('resize', this._herpositie);
    window.addEventListener('scroll', this._herpositie, true);
  }

  disconnectedCallback() {
    document.removeEventListener('mousedown', this._buiten, true);
    document.removeEventListener('keydown', this._toets);
    window.removeEventListener('resize', this._herpositie);
    window.removeEventListener('scroll', this._herpositie, true);
    cancelAnimationFrame(this._frame);
    super.disconnectedCallback();
  }

  get _tekst() {
    const g = this.gekozen || [];
    if (!g.length) return this.leeg;
    if (g.length === 1) return (this.items || []).find((i) => i.waarde === g[0])?.label ?? g[0];
    return `${g.length} ${this.meervoud}`;
  }

  _zetPlek() {
    const r = this.renderRoot.querySelector('.knop')?.getBoundingClientRect();
    if (!r) return;
    const breed = 340;
    const links = Math.max(8, Math.min(r.left, window.innerWidth - breed - 8));
    const ruimteOnder = window.innerHeight - r.bottom - 12;
    // Past het niet onder de knop, en is er boven meer ruimte, dan klapt het menu omhoog.
    const omhoog = ruimteOnder < 260 && r.top > ruimteOnder;
    this._plek = omhoog
      ? { links, onder: window.innerHeight - r.top + 6, max: r.top - 18 }
      : { links, boven: r.bottom + 6, max: ruimteOnder };
  }

  async _wissel() {
    if (this._open) { this._sluit(); return; }
    this._zetPlek();
    this._open = true;
    this._zoek = '';
    this._volg();
    await this.updateComplete;
    this.renderRoot.querySelector('.zoek input')?.focus();
  }

  _sluit() { this._open = false; }

  updated() {
    // In de bovenste laag zetten zodra het menu er staat (zie de kop van dit bestand).
    const menu = this.renderRoot.querySelector('.menu');
    if (menu && !menu.matches(':popover-open')) {
      try { menu.showPopover(); } catch (_) { /* oudere browser: dan gewoon fixed */ }
    }
  }

  _meld(gekozen, modus = this.modus) {
    this.dispatchEvent(new CustomEvent('wijzig', { detail: { gekozen, modus } }));
  }

  _vink(waarde) {
    const g = this.gekozen || [];
    this._meld(g.includes(waarde) ? g.filter((x) => x !== waarde) : [...g, waarde]);
  }

  render() {
    const g = this.gekozen || [];
    const aan = g.length > 0;
    return html`
      <button class="knop ${aan ? 'aan' : ''} ${this._open ? 'open' : ''}" @click=${this._wissel}
              aria-haspopup="listbox" aria-expanded=${this._open}>
        <span>${this._tekst}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8"
             stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"></path></svg>
      </button>
      ${this._open ? this._renderMenu() : ''}`;
  }

  _renderMenu() {
    const p = this._plek || {};
    const items = this.items || [];
    const lang = items.length > 8;
    const q = this._zoek.trim().toLowerCase();
    const zichtbaar = q ? items.filter((i) => String(i.label).toLowerCase().includes(q)) : items;
    // Groepskopjes alleen als er groepen zijn en je niet aan het zoeken bent.
    const metGroepen = !q && zichtbaar.some((i) => i.groep);
    const regels = [];
    let groep = null;
    for (const i of zichtbaar) {
      if (metGroepen && i.groep !== groep) { groep = i.groep; regels.push(html`<div class="groep">${groep}</div>`); }
      const vink = (this.gekozen || []).includes(i.waarde);
      regels.push(html`
        <button class="optie ${vink ? 'aan' : ''}" role="option" aria-selected=${vink} @click=${() => this._vink(i.waarde)}>
          <span class="vakje">${vink ? html`<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff"
            stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7"></path></svg>` : ''}</span>
          <span class="naam">${i.label}</span>
          ${i.aantal != null ? html`<span class="aantal">${i.aantal} ${i.aantal === 1 ? this.eenheidEen : this.eenheid}</span>` : ''}
        </button>`);
    }
    const stijl = `left:${p.links}px;${p.boven != null ? `top:${p.boven}px;` : `bottom:${p.onder}px;`}max-height:${Math.max(200, p.max)}px`;
    return html`
      <div class="menu" popover="manual" style=${stijl} role="listbox" aria-multiselectable="true">
        ${lang ? html`
          <label class="zoek">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
            <input .value=${this._zoek} placeholder=${this.zoekLabel} @input=${(e) => { this._zoek = e.target.value; }}>
          </label>` : ''}
        <div class="lijst">
          ${regels.length ? regels : html`<div class="leeg">${q ? 'Niets gevonden.' : 'Niets om uit te kiezen.'}</div>`}
        </div>
        <div class="voet">
          ${this.modus ? html`
            <span class="bevat">Laag bevat</span>
            <span class="segment">
              <button class=${this.modus !== 'all' ? 'aan' : ''} @click=${() => this._meld(this.gekozen || [], 'any')}>één ervan</button>
              <button class=${this.modus === 'all' ? 'aan' : ''} @click=${() => this._meld(this.gekozen || [], 'all')}>allemaal</button>
            </span>` : html`
            <button class="wis" ?disabled=${!(this.gekozen || []).length} @click=${() => this._meld([])}>Wissen</button>`}
          <span class="rek"></span>
          <button class="klaar" @click=${this._sluit}>Klaar</button>
        </div>
      </div>`;
  }

  static styles = css`
    :host { display:inline-flex; position:relative; }
    button { font-family:inherit; cursor:pointer; }
    /* --fk-hoogte / --fk-letter: om de knop even hoog te maken als een zoekbalk ernaast;
       --fk-min: een vaste minimale breedte, met het pijltje dan rechts. */
    .knop { display:inline-flex; align-items:center; justify-content:space-between; gap:8px; padding:6px 10px; border-radius:8px; box-sizing:border-box;
            height:var(--fk-hoogte, auto); min-width:var(--fk-min, 0); border:1.5px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text);
            font-size:var(--fk-letter, .76rem); font-weight:700; white-space:nowrap; max-width:var(--fk-max, 240px); }
    .knop span { overflow:hidden; text-overflow:ellipsis; }
    .knop svg { flex:none; color:var(--mv-text-sub); }
    .knop:hover, .knop.open { border-color:var(--mv-primary); }
    .knop.aan { border-color:var(--mv-primary); background:var(--mv-primary-tint); color:var(--mv-primary-hover); }
    .menu { position:fixed; inset:auto; margin:0; padding:0; z-index:60; width:340px; box-sizing:border-box; display:flex; flex-direction:column;
            border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface);
            box-shadow:0 14px 36px rgba(16,28,18,.18); overflow:hidden; }
    .zoek { display:flex; align-items:center; gap:8px; margin:10px 10px 6px; padding:0 10px; height:36px;
            border:1.5px solid var(--mv-primary); border-radius:8px; color:var(--mv-text-muted); }
    .zoek input { flex:1; min-width:0; border:none; outline:none; background:none; font:inherit; font-size:.82rem; color:var(--mv-text); }
    .lijst { flex:1; min-height:0; overflow-y:auto; padding:4px 0 6px; border-top:1px solid var(--mv-border-soft); }
    .groep { position:sticky; top:0; z-index:1; padding:8px 14px 4px; background:var(--mv-surface); font-size:.62rem; font-weight:800;
             letter-spacing:.08em; text-transform:uppercase; color:var(--mv-text-muted); }
    .optie { display:flex; align-items:center; gap:10px; width:100%; padding:8px 14px; border:none; border-radius:0;
             background:none; text-align:left; font-size:.82rem; color:var(--mv-text); }
    .optie:hover { background:var(--mv-surface-alt); }
    /* Wat aangevinkt is valt op: lichtgroen over de volle breedte, de naam vet. */
    .optie.aan { background:var(--mv-primary-light); }
    .optie.aan:hover { background:var(--mv-primary-tint); }
    .optie.aan .naam { font-weight:700; }
    .vakje { flex:none; width:16px; height:16px; box-sizing:border-box; border-radius:4px; border:1.5px solid var(--mv-border-strong, #c9d2c9);
             display:flex; align-items:center; justify-content:center; background:var(--mv-surface); }
    .optie.aan .vakje { background:var(--mv-primary); border-color:var(--mv-primary); }
    .naam { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .aantal { flex:none; font-size:.74rem; color:var(--mv-text-muted); }
    .leeg { padding:14px 8px; font-size:.78rem; color:var(--mv-text-muted); }
    .voet { display:flex; align-items:center; gap:8px; padding:10px 12px; border-top:1px solid var(--mv-border-soft);
            background:var(--mv-surface-alt); }
    .bevat { font-size:.78rem; color:var(--mv-text-sub); }
    .segment { display:inline-flex; border-radius:7px; overflow:hidden; border:1px solid var(--mv-border); }
    .segment button { padding:5px 10px; border:none; background:var(--mv-surface); font-size:.76rem; font-weight:700; color:var(--mv-text-sub); }
    .segment button.aan { background:var(--mv-text); color:var(--mv-surface); }
    .rek { flex:1; }
    .wis { border:none; background:none; padding:0; font-size:.76rem; font-weight:700; color:var(--mv-text-sub); }
    .wis:disabled { opacity:.4; cursor:default; }
    .klaar { padding:6px 14px; border:none; border-radius:8px; background:var(--mv-primary); color:#fff; font-size:.8rem; font-weight:700; }
    .klaar:hover { background:var(--mv-primary-hover); }
  `;
}

customElements.define('filter-keuze', FilterKeuze);
